/**
 * StepAllReviews (step 7) — summary table of all completed reviews this session.
 */
import { C, getRatingColor, getRatingLabel } from '../../../data/managerReviewData';
import type { CompletedReview } from './StepComplete';

interface StepAllReviewsProps {
  completedReviews: CompletedReview[];
  lastCompleted?: { managerFirst: string; employeeFirst: string } | null;
  onBack: () => void;
  onStartNew: () => void;
}

const RatingCell = ({ rating }: { rating: number | null }) => {
  if (!rating) return <span style={{ fontSize: 12, color: '#ccc' }}>—</span>;
  const col = getRatingColor(rating);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 8px', borderRadius: 5,
      background: col + '18', border: `1.5px solid ${col}`,
      color: col, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <span style={{ fontWeight: 900 }}>{rating}</span>
      <span style={{ fontWeight: 500, opacity: 0.8 }}>{getRatingLabel(rating)}</span>
    </span>
  );
};

const avg = (values: (number | null)[]) => {
  const nums = values.filter((v): v is number => v !== null);
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
};

export default function StepAllReviews({ completedReviews, lastCompleted, onBack, onStartNew }: StepAllReviewsProps) {
  const btnBase: React.CSSProperties = {
    fontFamily: 'Inter', fontWeight: 600, fontSize: 14,
    borderRadius: 8, padding: '10px 20px', cursor: 'pointer',
  };

  const avgDelivery = avg(completedReviews.map(r => r.deliveryRating));
  const avgBehaviors = avg(completedReviews.map(r => r.behaviorsRating));
  const avgOverall = avg(completedReviews.map(r => r.overallRating));
  const promoCount = completedReviews.filter(r => r.promotion === 'yes').length;

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '0 24px 80px',
    }}>

      {/* ── Top bar ── */}
      <div style={{
        width: '100%', maxWidth: 960,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 0 0',
      }}>
        <div style={{ color: '#fff', fontWeight: 900, fontSize: 18, letterSpacing: '0.04em' }}>FINN</div>
        <button
          onClick={onBack}
          style={{ ...btnBase, fontSize: 12, color: '#fff', background: 'none', border: '1px solid #555', padding: '5px 12px' }}
        >← Back</button>
      </div>

      {/* ── Header ── */}
      <div style={{ width: '100%', maxWidth: 1100, padding: '40px 0 24px' }}>
        {lastCompleted ? (
          <>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', letterSpacing: '-0.02em', marginBottom: 10 }}>
              Review complete, {lastCompleted.managerFirst}.
            </div>
            <div style={{ fontSize: 15, color: '#aaa', lineHeight: 1.7, maxWidth: 520, marginBottom: 4 }}>
              You've taken the time to give {lastCompleted.employeeFirst} a structured, thoughtful review. That matters more than you might think — clarity and candour are some of the most valuable things a manager can offer.
            </div>
          </>
        ) : (
          <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', letterSpacing: '-0.02em', marginBottom: 8 }}>
            Review Summary
          </div>
        )}
        <div style={{ fontSize: 14, color: '#888', marginTop: 8 }}>
          {completedReviews.length === 0
            ? 'No reviews completed yet.'
            : `${completedReviews.length} review${completedReviews.length > 1 ? 's' : ''} completed this session.`}
        </div>
      </div>

      {/* ── Table ── */}
      {completedReviews.length > 0 && (
        <div style={{
          width: '100%', maxWidth: 1100, background: '#fff',
          borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
        }}>
          {/* Header row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 70px 1fr 1fr 1fr 80px 100px',
            padding: '10px 24px', gap: 16, background: '#f5f5f5',
            borderBottom: '2px solid #e4e4e4',
          }}>
            {['Employee', 'Level', 'Delivery', 'Behaviors', 'Overall', 'Promotion', 'Completed'].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>

          {/* Data rows */}
          {completedReviews.map((r, i) => (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 70px 1fr 1fr 1fr 80px 100px',
              padding: '16px 24px', gap: 16, alignItems: 'center',
              borderBottom: i < completedReviews.length - 1 ? '1px solid #f0f0f0' : 'none',
              background: i % 2 === 1 ? '#fafafa' : '#fff',
            }}>
              {/* Employee */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', background: C.blue,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>{r.employee.avatar}</span>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.employee.name}</div>
                  <div style={{ fontSize: 11, color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.employee.role}</div>
                </div>
              </div>
              {/* Level */}
              <div style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>{r.level}</div>
              {/* Delivery */}
              <div><RatingCell rating={r.deliveryRating} /></div>
              {/* Behaviors */}
              <div><RatingCell rating={r.behaviorsRating} /></div>
              {/* Overall */}
              <div><RatingCell rating={r.overallRating} /></div>
              {/* Promotion */}
              <div style={{ fontSize: 13, fontWeight: 600, color: r.promotion === 'yes' ? C.green : r.promotion === 'no' || r.promotion === 'conditional' ? C.muted : '#ccc' }}>
                {r.promotion === 'yes' ? 'Yes' : r.promotion === 'conditional' ? 'Conditional (June 2027)' : r.promotion === 'no' ? 'No' : '—'}
              </div>
              {/* Date */}
              <div style={{ fontSize: 12, color: '#555', fontWeight: 500 }}>{r.completedAt}</div>
            </div>
          ))}

          {/* Footer totals */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 70px 1fr 1fr 1fr 80px 100px',
            padding: '12px 24px', gap: 16, alignItems: 'center',
            background: '#f5f5f5', borderTop: '2px solid #e4e4e4',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>
              {completedReviews.length} employee{completedReviews.length > 1 ? 's' : ''}
            </div>
            <div />
            <div style={{ fontSize: 11, color: C.muted }}>{avgDelivery ? `Avg ${avgDelivery.toFixed(1)}` : ''}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{avgBehaviors ? `Avg ${avgBehaviors.toFixed(1)}` : ''}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{avgOverall ? `Avg ${avgOverall.toFixed(1)}` : ''}</div>
            <div style={{ fontSize: 11, color: C.muted }}>
              {promoCount > 0 ? `${promoCount} promoted` : '—'}
            </div>
            <div />
          </div>
        </div>
      )}

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', paddingTop: 32, flexWrap: 'wrap' }}>
        <button
          onClick={onStartNew}
          style={{ ...btnBase, color: '#fff', background: C.blue, border: 'none' }}
        >Start a new review →</button>
      </div>

    </div>
  );
}
